import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Search,
  Typography,
  useI18nContext,
} from '@procore/core-react';
import classNames from 'classnames';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { FormControlLabel } from '@mui/material';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';
import { useStyles } from './EnhancedFilters.styles';
import { isIndeterminate } from '../utils/tradesBulkCheckboxUtil';
import { Filter } from '../../helpers/filters';

interface HighlightedTextProps {
  text: string;
  searchTerm: string;
}

const HighlightedText = memo(({ text, searchTerm }: HighlightedTextProps) => {
  const classes = useStyles();
  const parts = searchTerm
    ? text.split(new RegExp(`(${searchTerm})`, 'gi'))
    : [text];
  return (
    <Typography intent="body">
      {parts.map((part, index) => (
        <span
          data-test-id="highlighted-text-part"
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className={
            searchTerm && part.toLowerCase() === searchTerm.toLowerCase()
              ? classes.highlightedText
              : null
          }
        >
          {part}
        </span>
      ))}
    </Typography>
  );
});

type FilterCheckboxProps = React.FC<{
  eventText: string;
  dataTrackClick: string;
  item: Filter;
  updateCheckbox: Function;
  isSelected: boolean;
  immutable: boolean;
  indeterminate?: boolean;
  searchTerm: string;
  label: string;
}>;

const FilterCheckbox: FilterCheckboxProps = memo(
  ({
    eventText,
    dataTrackClick,
    item,
    updateCheckbox,
    isSelected,
    immutable,
    indeterminate,
    searchTerm,
    label,
  }) => {
    const classes = useStyles();

    const onUpdateCheckbox = useCallback(() => {
      updateCheckbox(item);
    }, [item, updateCheckbox]);

    const itemKey = item.key;
    return (
      <div className={classes.checkboxWrapper} data-test-id="checkbox-wrapper">
        <FormControlLabel
          control={
            <AnalyticsWrappedComponent
              className="A11yException"
              componentType={Checkbox}
              eventText={eventText}
              eventLabel={itemKey}
              tabIndex={0}
              data-track-click={`${dataTrackClick}, Update Checkbox, ${itemKey}`}
              onChange={onUpdateCheckbox}
              value={itemKey}
              disabled={immutable}
              checked={isSelected}
              indeterminate={indeterminate}
            />
          }
          label={
            <HighlightedText
              data-test-id="highlighted-text"
              text={label}
              searchTerm={searchTerm}
            />
          }
        />
      </div>
    );
  }
);
FilterCheckbox.displayName = 'FilterCheckbox';

type CheckboxSublistSetProps = React.FC<{
  sublistPropertyName?: string;
  analyticsEventText: string;
  dataTrackClick: string;
  updateCheckbox: Function;
  selectedFilters: Array<any>;
  searchTerm: string;
  parent: Filter;
  containsSearchTerm: Function;
}>;

const CheckboxSublistSet: CheckboxSublistSetProps = memo(
  ({
    sublistPropertyName,
    analyticsEventText,
    dataTrackClick,
    updateCheckbox,
    selectedFilters,
    searchTerm,
    parent,
    containsSearchTerm,
  }) => {
    return (
      <>
        {parent[sublistPropertyName]
          .filter(containsSearchTerm)
          .map((sublistItem) => (
            <FilterCheckbox
              data-test-id="sublist-checkbox"
              data-test-item-id={`sublist-checkbox-${sublistItem.key}`}
              eventText={analyticsEventText}
              dataTrackClick={dataTrackClick}
              key={parent.key + sublistItem.key}
              item={sublistItem}
              updateCheckbox={updateCheckbox}
              isSelected={selectedFilters.some(
                (v) => v.key === sublistItem.key
              )}
              immutable={selectedFilters.some(
                (v) => v.key === sublistItem.key && v.immutable
              )}
              label={sublistItem.label}
              searchTerm={searchTerm}
            />
          ))}
      </>
    );
  }
);
CheckboxSublistSet.displayName = 'CheckboxSublistSet';

type CheckboxSetProps = React.FC<{
  filtersToShow: Array<any>;
  sublistPropertyName?: string;
  analyticsEventText: string;
  dataTrackClick: string;
  updateCheckbox: Function;
  selectedFilters: Array<any>;
  searchTerm: string;
  containsSearchTerm: (filter: Filter) => boolean;
}>;

const CheckboxSet: CheckboxSetProps = memo(
  ({
    filtersToShow,
    sublistPropertyName,
    analyticsEventText,
    dataTrackClick,
    updateCheckbox,
    selectedFilters,
    searchTerm,
    containsSearchTerm,
  }) => {
    const [sublistsOpen, setSublistsOpen] = useState([]);
    const classes = useStyles();

    const isItemExpanded = useCallback(
      (item) => {
        return !!searchTerm || sublistsOpen.includes(item);
      },
      [searchTerm, sublistsOpen]
    );
    const handleClickExpandCollapse = useCallback(
      (item) => {
        setSublistsOpen(
          isItemExpanded(item)
            ? sublistsOpen.filter((s) => s.key !== item.key)
            : [...sublistsOpen, item]
        );
      },
      [setSublistsOpen, sublistsOpen, isItemExpanded]
    );

    return (
      <>
        {filtersToShow.filter(containsSearchTerm).map((item) => (
          <div key={`box-${item.key}`}>
            <Box display="flex" alignItems="flex-start">
              {sublistPropertyName && (
                <Box
                  data-test-id="sublist-chevron"
                  data-test-item-id={`sublist-chevron-${item.key}`}
                  className={classNames(
                    classes.collapseArrow,
                    item[sublistPropertyName]?.length > 0
                      ? classes.sublistChevron
                      : classes.sublistChevronHidden
                  )}
                  onClick={() => {
                    handleClickExpandCollapse(item);
                  }}
                >
                  {isItemExpanded(item) ? <ExpandMore /> : <ChevronRight />}
                </Box>
              )}
              <FilterCheckbox
                data-test-id="list-checkbox"
                eventText={analyticsEventText}
                dataTrackClick={dataTrackClick}
                item={item}
                updateCheckbox={updateCheckbox}
                isSelected={selectedFilters.some((v) => v.key === item.key)}
                immutable={selectedFilters.some(
                  (v) => v.key === item.key && v.immutable
                )}
                indeterminate={isIndeterminate(
                  item,
                  sublistPropertyName,
                  selectedFilters
                )}
                label={item.label}
                searchTerm={searchTerm}
              />
            </Box>

            {sublistPropertyName && (
              <Box ml={6.5} key={`sublist-box-${item.key}`}>
                <Collapse in={isItemExpanded(item)} mountOnEnter unmountOnExit>
                  <CheckboxSublistSet
                    sublistPropertyName={sublistPropertyName}
                    parent={item}
                    analyticsEventText={analyticsEventText}
                    dataTrackClick={dataTrackClick}
                    updateCheckbox={updateCheckbox}
                    selectedFilters={selectedFilters.filter((f) => {
                      return f.key !== item.key;
                    })}
                    searchTerm={searchTerm}
                    containsSearchTerm={containsSearchTerm}
                  />
                </Collapse>
              </Box>
            )}
          </div>
        ))}
      </>
    );
  }
);
CheckboxSet.displayName = 'CheckboxSet';

export interface FilterCheckboxesProps {
  sectionName: string;
  updateCheckbox: Function;
  FiltersList: Array<any>;
  selectedFilters: Array<any>;
  dataTrackClick: string;
  analyticsEventText: string;
  showSearch?: boolean;
  sublistPropertyName?: string;
}

const FilterCheckboxes = memo(
  ({
    sectionName,
    updateCheckbox,
    FiltersList,
    selectedFilters,
    dataTrackClick,
    analyticsEventText,
    showSearch,
    sublistPropertyName,
  }: FilterCheckboxesProps) => {
    const maxToShow = 13;
    const [showAll, setShowAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const I18n = useI18nContext();
    const classes = useStyles();

    const containsSearchTerm = useCallback(
      (item) => {
        return (
          item.label.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1 ||
          item[sublistPropertyName]?.filter(
            (f) => f.label.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1
          ).length > 0
        );
      },
      [FiltersList, searchTerm, sublistPropertyName]
    );

    const onSearchTermChanged = useCallback((event) => {
      setSearchTerm(event.target.value);
    }, []);

    const allFiltersWithSearchApplied = useMemo(() => {
      return FiltersList.filter(containsSearchTerm);
    }, [FiltersList, searchTerm, containsSearchTerm]);

    return (
      <Box className={classes.checkboxContainer}>
        {showSearch && (
          <Box pb={2}>
            <Search
              onChange={onSearchTermChanged}
              onClear={() => {
                setSearchTerm('');
              }}
              placeholder={I18n.t('components.filters.search')}
            />
          </Box>
        )}
        <CheckboxSet
          filtersToShow={FiltersList.slice(0, maxToShow)}
          sublistPropertyName={sublistPropertyName}
          updateCheckbox={updateCheckbox}
          selectedFilters={selectedFilters.filter((f) => {
            return FiltersList.slice(0, maxToShow).some(
              (f2) =>
                f2.key === f.key ||
                f2[sublistPropertyName]?.some((f3) => f3.key === f.key)
            );
          })}
          dataTrackClick={dataTrackClick}
          analyticsEventText={analyticsEventText}
          searchTerm={searchTerm}
          containsSearchTerm={containsSearchTerm}
        />
        {FiltersList.length > maxToShow && (
          <Collapse
            in={showAll || !!searchTerm}
            data-test-id="hidden-checkboxes"
            mountOnEnter
            unmountOnExit
          >
            <CheckboxSet
              filtersToShow={FiltersList.slice(maxToShow, FiltersList.length)}
              sublistPropertyName={sublistPropertyName}
              updateCheckbox={updateCheckbox}
              selectedFilters={selectedFilters.filter((f) => {
                return FiltersList.slice(maxToShow, FiltersList.length).some(
                  (f2) =>
                    f2.key === f.key ||
                    f2[sublistPropertyName]?.some((f3) => f3.key === f.key)
                );
              })}
              dataTrackClick={dataTrackClick}
              analyticsEventText={analyticsEventText}
              searchTerm={searchTerm}
              containsSearchTerm={containsSearchTerm}
            />
          </Collapse>
        )}
        {allFiltersWithSearchApplied.length > maxToShow && (
          <>
            {!showAll && FiltersList.length > maxToShow && (
              <AnalyticsWrappedComponent
                eventText={`Clicked ${sectionName} Show More Filters Button`}
                eventLabel={dataTrackClick}
                componentType={Button}
                data-test-id="show-more-button"
                variant="secondary"
                onClick={() => {
                  setShowAll(true);
                }}
              >
                {I18n.t('components.filters.show_more_count', {
                  count: allFiltersWithSearchApplied.length - maxToShow,
                })}
              </AnalyticsWrappedComponent>
            )}
            {showAll && FiltersList.length > maxToShow && (
              <AnalyticsWrappedComponent
                eventText={`Clicked ${sectionName} Show Less Filters Button`}
                eventLabel={dataTrackClick}
                componentType={Button}
                data-test-id="show-less-button"
                variant="secondary"
                onClick={() => {
                  setShowAll(false);
                }}
              >
                {I18n.t('components.filters.show_less')}
              </AnalyticsWrappedComponent>
            )}
          </>
        )}
      </Box>
    );
  }
);
FilterCheckboxes.displayName = 'FilterCheckboxes';

export default FilterCheckboxes;
